import type { LucideIcon } from 'lucide-react';
import { Bug, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import type { SectionType } from '@/lib/changelog/changelog-types';

export type ChangelogSectionVisual = {
  type: SectionType;
  label: string;
  icon: LucideIcon;
  /** Cor de destaque (hex) — editor, prévia e bullets. */
  accent: string;
  /** Classes Tailwind alinhadas à página pública `/changelog`. */
  colorClass: string;
  bgClass: string;
  borderClass: string;
  badgeBgClass: string;
  badgeTextClass: string;
};

/** Fonte única de ícones/cores das secções do changelog (público + painel). */
export const CHANGELOG_SECTION_VISUALS: Record<SectionType, ChangelogSectionVisual> = {
  new: {
    type: 'new',
    label: 'Novidades',
    icon: Sparkles,
    accent: '#2563eb',
    colorClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-100',
    badgeBgClass: 'bg-blue-100',
    badgeTextClass: 'text-blue-700',
  },
  powers: {
    type: 'powers',
    label: 'Powers',
    icon: Zap,
    accent: '#7c3aed',
    colorClass: 'text-violet-600',
    bgClass: 'bg-violet-50',
    borderClass: 'border-violet-100',
    badgeBgClass: 'bg-violet-100',
    badgeTextClass: 'text-violet-700',
  },
  improvement: {
    type: 'improvement',
    label: 'Melhorias',
    icon: TrendingUp,
    accent: '#059669',
    colorClass: 'text-emerald-600',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-100',
    badgeBgClass: 'bg-emerald-100',
    badgeTextClass: 'text-emerald-700',
  },
  fix: {
    type: 'fix',
    label: 'Correções de bugs',
    icon: Bug,
    accent: '#e11d48',
    colorClass: 'text-rose-600',
    bgClass: 'bg-rose-50',
    borderClass: 'border-rose-100',
    badgeBgClass: 'bg-rose-100',
    badgeTextClass: 'text-rose-700',
  },
  aces: {
    type: 'aces',
    label: 'Aces',
    icon: AceNavIcon,
    accent: '#f59e0b',
    colorClass: 'text-amber-600',
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-100',
    badgeBgClass: 'bg-amber-100',
    badgeTextClass: 'text-amber-700',
  },
};

export const CHANGELOG_SECTION_TYPE_ORDER: SectionType[] = [
  'new',
  'improvement',
  'fix',
  'powers',
  'aces',
];

export function getChangelogSectionVisual(type: SectionType): ChangelogSectionVisual {
  return CHANGELOG_SECTION_VISUALS[type] ?? CHANGELOG_SECTION_VISUALS.new;
}

export function inferSectionTypeFromLabel(label: string): SectionType {
  const n = label.trim().toLowerCase();
  if (n.includes('ace')) return 'aces';
  if (n.includes('power')) return 'powers';
  if (n.includes('melhor') || n.includes('improve') || n.includes('qol')) return 'improvement';
  if (n.includes('bug') || n.includes('corre') || n.includes('fix')) return 'fix';
  if (n.includes('novidad') || n.includes('new') || n.includes('lanç')) return 'new';
  return 'new';
}
